import { useMemo, type JSX } from 'react'
import { ClipboardList, CheckCircle2, FileText, Image as ImageIcon, Paperclip, ExternalLink } from 'lucide-react'

export interface FormSubmissionField {
  id?: string | undefined
  label: string
  value?: unknown
  type?: string | undefined
  required?: boolean | undefined
  helpText?: string | undefined
  placeholder?: string | undefined
  options?: Array<{ label: string; value: string }> | undefined
}

export interface FormAttachmentItem {
  id: string
  filename: string
  mimeType?: string | undefined
  sizeBytes?: number | undefined
  url?: string | undefined
}

export interface PortalFormSubmissionCardProps {
  formName?: string | undefined
  formDescription?: string | null | undefined
  fields?: FormSubmissionField[] | undefined
  rawBody?: string | undefined
  attachments?: FormAttachmentItem[] | undefined
  onAttachmentClick?: ((attachmentId: string) => void) | undefined
}

function formatBytes(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function normalizeHtmlToLines(raw: string): string[] {
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
}

function isConversationRefField(label?: string, id?: string, type?: string): boolean {
  if (type === 'conversation_ref') return true
  if (id && /(?:conversation|ticket|complaint)\s*(?:id|ref|reference)?/i.test(id)) return true
  if (label && /(?:conversation|ticket|complaint)\s*(?:id|ref|reference)?/i.test(label)) return true
  return false
}

/**
 * Checks whether a message body or payload represents a portal form submission.
 */
export function isPortalFormSubmission(
  portalForm?: { formName?: string; submissionId?: string | null } | null,
  body?: string | null,
): boolean {
  if (portalForm && (portalForm.formName || portalForm.submissionId)) return true
  if (!body) return false
  return (
    body.includes('Form submitted —') ||
    body.includes('Form submitted -') ||
    body.startsWith('Form submitted')
  )
}

/**
 * Renders an exact form UI for portal form submissions within conversation timelines.
 */
export function PortalFormSubmissionCard({
  formName: propFormName,
  formDescription: propFormDescription,
  fields: propFields,
  rawBody,
  attachments = [],
  onAttachmentClick,
}: PortalFormSubmissionCardProps): JSX.Element {
  const { formName, formDescription, fields } = useMemo(() => {
    // 1. If rich fields are provided directly from portal_form_submissions
    if (propFields && propFields.length > 0) {
      return {
        formName: propFormName || 'Form Submission',
        formDescription: propFormDescription ?? null,
        fields: propFields.filter((f) => !isConversationRefField(f.label, f.id, f.type)),
      }
    }

    // 2. Otherwise parse from plain text body
    const lines = normalizeHtmlToLines(rawBody || '')
    let parsedName = propFormName || 'Portal Form Submission'
    let parsedDesc: string | null = propFormDescription ?? null
    const parsedFields: FormSubmissionField[] = []

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      if (!line) continue
      if (i === 0 && (line.startsWith('Form submitted —') || line.startsWith('Form submitted -') || line.startsWith('Form submitted'))) {
        // e.g. "Form submitted — Keg Issue - Outlet Checklist Form (submission 8a515ef0...)"
        const match = line.match(/^Form submitted\s*[—-]\s*(.*?)(?:\s*\([sS]ubmission\s+[a-f0-9-]+\))?$/i)
        if (match?.[1]?.trim()) {
          parsedName = match[1].trim()
        }
        continue
      }

      const colonIdx = line.indexOf(':')
      if (colonIdx > 0) {
        const label = line.slice(0, colonIdx).trim()
        if (isConversationRefField(label)) {
          continue
        }
        const value = line.slice(colonIdx + 1).trim()
        const isFile = /attachment|file/i.test(label) || /file.*attached/i.test(value)
        parsedFields.push({
          id: `field_${i}`,
          label,
          value,
          type: isFile ? 'file' : 'text',
        })
      }
    }

    return {
      formName: parsedName,
      formDescription: parsedDesc,
      fields: parsedFields,
    }
  }, [propFormName, propFormDescription, propFields, rawBody])

  return (
    <div className="my-2.5 max-w-2xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
      {/* Form Header */}
      <div className="border-b border-slate-100 bg-slate-50/70 px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#3366FF]/10 text-[#3366FF]">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 leading-snug">
                {formName}
              </h3>
              {formDescription && (
                <p className="mt-0.5 text-xs text-slate-500">{formDescription}</p>
              )}
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-600/20">
            <CheckCircle2 className="h-3 w-3" /> Submitted
          </span>
        </div>
      </div>

      {/* Form Fields Body */}
      <div className="space-y-4 p-5 bg-white">
        {fields.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No field data recorded in this submission.</p>
        ) : (
          fields.map((field, idx) => {
            const isFile = field.type === 'file' || /attachment|file/i.test(field.label)
            const isTextarea = field.type === 'textarea'
            const isArray = Array.isArray(field.value)

            return (
              <div key={field.id || `f_${idx}`} className="space-y-1.5">
                <label className="block text-xs font-semibold text-slate-700">
                  {field.label}
                  {field.required && <span className="ml-0.5 font-bold text-red-500">*</span>}
                </label>

                {isFile ? (
                  attachments.length > 0 ? (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {attachments.map((att) => {
                        const isImg = att.mimeType?.startsWith('image/')
                        return (
                          <div
                            key={att.id}
                            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2 text-xs text-slate-700 shadow-2xs transition-colors hover:border-slate-300"
                          >
                            {isImg ? (
                              <ImageIcon className="h-4 w-4 text-indigo-500 shrink-0" />
                            ) : (
                              <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
                            )}
                            <span className="font-medium truncate max-w-[180px]">{att.filename}</span>
                            {att.sizeBytes && (
                              <span className="text-[11px] text-slate-400">({formatBytes(att.sizeBytes)})</span>
                            )}
                            {onAttachmentClick ? (
                              <button
                                type="button"
                                onClick={() => onAttachmentClick(att.id)}
                                className="ml-1 text-[#3366FF] hover:underline flex items-center gap-0.5 text-[11px]"
                              >
                                View <ExternalLink className="h-3 w-3" />
                              </button>
                            ) : att.url ? (
                              <a
                                href={att.url}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-1 text-[#3366FF] hover:underline flex items-center gap-0.5 text-[11px]"
                              >
                                View <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="rounded-md border border-slate-200 bg-slate-50/50 px-3 py-2 text-xs text-slate-500">
                      {typeof field.value === 'string' && field.value.trim()
                        ? field.value
                        : 'No files attached'}
                    </div>
                  )
                ) : isTextarea ? (
                  <div className="min-h-[70px] w-full select-text whitespace-pre-wrap rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-800 shadow-2xs">
                    {String(field.value ?? '') || <span className="text-slate-400 italic">—</span>}
                  </div>
                ) : isArray ? (
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {(field.value as unknown[]).map((val, vIdx) => (
                      <span
                        key={vIdx}
                        className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                      >
                        <CheckCircle2 className="h-3 w-3 text-blue-600" />
                        {String(val)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="w-full select-text rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-800 shadow-2xs">
                    {String(field.value ?? '') || <span className="text-slate-400 italic">—</span>}
                  </div>
                )}

                {field.helpText && (
                  <p className="mt-1 text-[11px] text-slate-400">{field.helpText}</p>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default PortalFormSubmissionCard
